import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService, campusService } from '../api';
import toast from 'react-hot-toast';
import { ArrowRight, Building2, Globe, Mail, Sparkles } from 'lucide-react';

function formatApiError(err) {
    const data = err?.response?.data;
    if (!data) return 'Registration failed.';
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.detail) return data.detail;
    if (data.error) return data.error;
    const key = Object.keys(data)[0];
    if (!key) return 'Registration failed.';
    const val = data[key];
    return Array.isArray(val) ? val[0] : String(val);
}

function Register() {
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('invite') || '';
    const inviteEmail = searchParams.get('email') || '';

    const [registrationMode, setRegistrationMode] = useState(
        inviteToken ? 'organization' : 'individual'
    );
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        username: '',
        email: inviteEmail,
        password: '',
        password_confirm: '',
        org_code: '',
    });
    const [inviteInfo, setInviteInfo] = useState(null);
    const [orgPreview, setOrgPreview] = useState(null);
    const [codeChecked, setCodeChecked] = useState(false);
    const [validatingCode, setValidatingCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const verifyInvite = useCallback(async () => {
        if (!inviteToken) return;
        try {
            const res = await campusService.verifyInvite(inviteToken, formData.email || undefined);
            setInviteInfo(res.data);
            setRegistrationMode('organization');
            if (res.data?.email) {
                setFormData((prev) => ({ ...prev, email: res.data.email }));
            }
        } catch {
            toast.error('This invitation link is invalid or has expired.');
        }
    }, [inviteToken, formData.email]);

    useEffect(() => {
        if (inviteToken) verifyInvite();
    }, [inviteToken, verifyInvite]);

    const handleOrgCodeBlur = async () => {
        const code = formData.org_code.trim();
        if (!code || registrationMode !== 'organization' || inviteToken) return;
        setValidatingCode(true);
        setCodeChecked(true);
        try {
            const res = await campusService.verifyRegistrationCode(code);
            setOrgPreview(res.data);
        } catch {
            setOrgPreview({ valid: false });
        } finally {
            setValidatingCode(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.password_confirm) {
            return toast.error('Passwords do not match');
        }
        if (registrationMode === 'organization' && !inviteToken && !formData.org_code.trim()) {
            return toast.error('Enter your organisation code or use an invite link.');
        }

        setLoading(true);
        try {
            const payload = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                username: formData.username,
                email: formData.email,
                password: formData.password,
                password_confirm: formData.password_confirm,
                registration_mode: registrationMode,
            };
            if (inviteToken) {
                payload.invite_token = inviteToken;
            } else if (registrationMode === 'organization') {
                payload.org_code = formData.org_code.trim();
            }

            await authService.register(payload);
            toast.success(
                registrationMode === 'organization'
                    ? 'Account created! Enter the code we sent to verify your email.'
                    : 'Account created! Enter the code we sent to verify your email.'
            );
            navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email.trim())}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-bg-primary)',
        outline: 'none',
        transition: 'border-color var(--transition-fast)',
        color: 'var(--color-text-primary)',
        width: '100%',
    };

    return (
        <div className="flex bg-bg-primary register-page" style={{ minHeight: '100vh', width: '100%' }}>
            <div
                className="flex items-center justify-center register-form-column"
                style={{
                    flex: 1,
                    padding: 'var(--spacing-2xl)',
                    backgroundColor: 'var(--color-card-bg)',
                    color: 'var(--color-text-primary)',
                }}
            >
                <div style={{ width: '100%', maxWidth: '520px' }}>
                    <Link to="/" className="flex items-center gap-xs" style={{ marginBottom: 'var(--spacing-2xl)', textDecoration: 'none' }}>
                        <div className="register-logo-mark">L</div>
                        <span style={{ fontWeight: 800, fontSize: '1.6rem', color: 'var(--color-text-primary)', letterSpacing: '-0.5px' }}>Learnly</span>
                    </Link>

                    <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                        <h1 style={{ fontSize: '2.5rem', marginBottom: 'var(--spacing-xs)', lineHeight: 1.2 }}>Create account</h1>
                        <p className="text-muted" style={{ fontSize: '1.05rem' }}>
                            Join the public marketplace or connect to your organisation.
                        </p>
                    </div>

                    {inviteInfo?.valid && (
                        <div className="register-invite-banner">
                            <Mail size={18} />
                            <div>
                                <strong>Organisation invite</strong>
                                <p>You&apos;re joining <span>{inviteInfo.organization_name}</span></p>
                            </div>
                        </div>
                    )}

                    {!inviteToken && (
                        <div className="register-mode-picker" role="tablist" aria-label="Registration type">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={registrationMode === 'individual'}
                                className={`register-mode-option${registrationMode === 'individual' ? ' is-active' : ''}`}
                                onClick={() => {
                                    setRegistrationMode('individual');
                                    setOrgPreview(null);
                                }}
                            >
                                <Globe size={20} />
                                <span className="register-mode-title">Individual learner</span>
                                <span className="register-mode-desc">Browse the public Learnly marketplace</span>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={registrationMode === 'organization'}
                                className={`register-mode-option${registrationMode === 'organization' ? ' is-active' : ''}`}
                                onClick={() => setRegistrationMode('organization')}
                            >
                                <Building2 size={20} />
                                <span className="register-mode-title">Organisation member</span>
                                <span className="register-mode-desc">I have a join code from my school or company</span>
                            </button>
                        </div>
                    )}

                    <form className="flex flex-col gap-md" onSubmit={handleSubmit}>
                        {registrationMode === 'organization' && !inviteToken && (
                            <div className="flex flex-col gap-xs">
                                <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>
                                    Organisation code
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.org_code}
                                    onChange={(e) => {
                                        setFormData({ ...formData, org_code: e.target.value.toUpperCase() });
                                        setOrgPreview(null);
                                        setCodeChecked(false);
                                    }}
                                    onBlur={handleOrgCodeBlur}
                                    placeholder="e.g. JALUSILEARNLY"
                                    style={inputStyle}
                                />
                                {validatingCode && <p className="text-muted text-sm">Checking code…</p>}
                                {codeChecked && orgPreview?.valid && (
                                    <p className="register-org-valid">
                                        <Sparkles size={14} /> Joining {orgPreview.organization_name}
                                    </p>
                                )}
                                {codeChecked && orgPreview && !orgPreview.valid && (
                                    <p className="text-sm" style={{ color: 'var(--color-error)' }}>Code not recognised — check with your admin.</p>
                                )}
                            </div>
                        )}

                        <div className="flex gap-md">
                            <div className="flex flex-col gap-xs" style={{ flex: 1 }}>
                                <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>First name</label>
                                <input type="text" required value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="John" style={inputStyle} />
                            </div>
                            <div className="flex flex-col gap-xs" style={{ flex: 1 }}>
                                <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Last name</label>
                                <input type="text" required value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Doe" style={inputStyle} />
                            </div>
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Username</label>
                            <input type="text" required value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="johndoe" style={inputStyle} />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Email</label>
                            <input
                                type="email"
                                required
                                readOnly={!!inviteInfo?.email}
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="john@example.com"
                                style={{ ...inputStyle, opacity: inviteInfo?.email ? 0.85 : 1 }}
                            />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Password</label>
                            <input type="password" required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="••••••••" style={inputStyle} />
                        </div>

                        <div className="flex flex-col gap-xs">
                            <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Confirm password</label>
                            <input type="password" required value={formData.password_confirm} onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })} placeholder="••••••••" style={inputStyle} />
                        </div>

                        <button type="submit" disabled={loading} className="btn btn-primary shadow-md flex items-center justify-center gap-sm w-full" style={{ padding: '16px', fontSize: '1.1rem', marginTop: 'var(--spacing-sm)' }}>
                            {loading ? 'Creating account…' : <>Create account <ArrowRight size={20} /></>}
                        </button>
                    </form>

                    <p className="text-center text-muted" style={{ marginTop: 'var(--spacing-xl)', fontSize: '1rem' }}>
                        Already have an account? <Link to="/auth/login" style={{ color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
                    </p>
                </div>
            </div>

            <div className="auth-photo-rail register-hero-column">
                <aside className="auth-photo-panel register-hero-panel" aria-label="Learnly learning environment">
                    <img
                        className="auth-photo-panel__media"
                        src="/auth_panel.webp"
                        alt="Desk with notebook, books, and laptop by a window"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        width="800"
                        height="1000"
                    />
                    <div className="auth-photo-panel__scrub register-hero-mesh" aria-hidden="true" />
                    <div className="auth-photo-panel__copy register-hero-content">
                        <span className="auth-photo-panel__kicker">Jalusi Learnly</span>
                        <h2>Learn openly. Join your organisation when you&apos;re ready.</h2>
                        <p>
                            Individual accounts get the public course marketplace. Organisation codes and email invites unlock your company or campus catalogue — without losing access to Learnly Public.
                        </p>
                    </div>
                </aside>
            </div>
        </div>
    );
}

export default Register;
